import { Grid } from '@mui/material';
import { CustomerProductNumberDropdown } from 'new/customer-product-numbers-dropdown';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { AppBadge } from 'shared/components/app-badge';
import {
  discountRecommendationBrands,
  listPriceBrands,
  PRODUCT_ID_REGEX,
  quantityBreakMessage,
} from 'shared/constants';
import {
  ExchangeFormTextField,
  FormTextField,
  NumberTextField,
  ProductAutocomplete,
  ProductDropdown,
  SelectField,
  VendorAutocomplete,
} from 'shared/controls';
import { useSelectFormProduct, useSelectFormVendor } from 'shared/hooks';
import { useExchangeRate, useFactorSheetName, useProductTypes } from 'shared/queries';
import { Brands, ProductForm } from 'shared/types';
import { buildCostHelperText, buildListPriceHelperText } from 'shared/utility';

interface NewProductFieldsProps {
  customerId: string;
  isRecommendation?: boolean;
}

export function NewProductFields({ customerId, isRecommendation }: NewProductFieldsProps) {
  const {
    formState: { errors },
    watch,
  } = useFormContext<ProductForm>();
  const { brand } = useParams<{ brand: Brands }>();
  const isPartsVsEquipmentAvailable = ['lewis', 'inscoprocess'].includes(brand);
  const isDiscountBasedBrand: boolean = discountRecommendationBrands.includes(brand);
  const isListPriceBrand: boolean = listPriceBrands.includes(brand);
  const isInscoProccess = brand === 'inscoprocess';
  const isCBEbrand: boolean = brand === 'cbe';
  const isNianticBrand: boolean = brand === 'nianticseal';

  const isNew = watch('isNew');
  const productExchangeRate = watch('exchangerate');
  const currencyType = watch('currencytype');
  const newVendor = watch('newVendor');
  const productId = watch('itemId');
  const quantityBreakFlag = watch('quantityBreakFlag');
  const costEffectiveDate = watch('costEffectiveDate');
  const listPriceEffectiveDate = watch('listPriceEffectiveDate');

  const [onProductSelect, onProductClear] = useSelectFormProduct();
  const [onVendorSelect, onVendorClear] = useSelectFormVendor();

  const selectedCurrency = isNew ? currencyType : '';
  const { exchangeRate } = useExchangeRate(selectedCurrency ?? '');

  const costHelperText = buildCostHelperText(costEffectiveDate, brand);
  const listPriceHelperText = buildListPriceHelperText(listPriceEffectiveDate);

  return (
    <Grid container spacing={1} pt={1}>
      {!isRecommendation && (
        <>
          <Grid xs={12} item>
            <ProductAutocomplete
              name='name'
              onSelect={onProductSelect}
              onClear={onProductClear}
              defaultValue=''
              error={Boolean(errors?.name)}
              helperText={errors?.name?.message}
            />
          </Grid>
          <Grid xs={12} item>
            <FormTextField
              name='itemId'
              label='Product Number'
              rules={{
                required: { message: 'Product Number is required', value: true },
                pattern: { message: `Single and double quotes are not allowed`, value: PRODUCT_ID_REGEX },
              }}
              error={Boolean(errors?.itemId)}
              helperText={errors?.itemId?.message}
              disabled={!isNew}
            />
          </Grid>
          <Grid xs={12} item>
            <FormTextField name='extendedDesc' label='Product Extended Description' disabled={!isNew} />
          </Grid>
        </>
      )}
      <Grid xs={12} item>
        <ProductDropdown
          errorMessage='Product Type is required'
          useQuery={useProductTypes}
          label='Product Type'
          defaultValue=''
          name='productType'
          error={Boolean(errors?.productType)}
          helperText={errors?.productType?.message}
        />
      </Grid>
      <Grid xs={12} item>
        <NumberTextField
          name='quantity'
          label='Quantity'
          rules={{
            required: {
              message: 'Quantity is required',
              value: true,
            },
            min: {
              message: 'Quantity must be greater than 0',
              value: 1,
            },
          }}
          error={Boolean(errors?.quantity)}
          helperText={errors?.quantity?.message}
        />
      </Grid>
      {!isRecommendation && (
        <Grid xs={12} item>
          <AppBadge title={quantityBreakMessage} badgeContent='!' invisible={!quantityBreakFlag} fullWidth>
            <>
              <NumberTextField
                name='lastCostPaid'
                label='Cost'
                prefix='$'
                rules={{
                  required: {
                    message: 'Cost is required',
                    value: isNew,
                  },
                  min: {
                    message: 'Cost must be greater than 0',
                    value: 0.001,
                  },
                }}
                sx={
                  isCBEbrand
                    ? {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderBottomRightRadius: '0',
                          borderTopRightRadius: '0',
                        },
                      }
                    : undefined
                }
                error={Boolean(errors?.lastCostPaid)}
                helperText={errors?.lastCostPaid?.message || costHelperText}
                decimalScale={isNianticBrand ? 4 : 2}
                formatDecimalsOnBlur={isNianticBrand}
              />
              {isCBEbrand && (
                <SelectField
                  name='currencytype'
                  label='Cost Currency'
                  options={['CAD', 'USD']}
                  getOptionLabel={option => option}
                  getOptionSelected={(option, value) => option === value}
                  disabled={!isNew}
                  rules={{
                    required: {
                      message: 'Cost Currency is required',
                      value: isNew,
                    },
                  }}
                  sx={{
                    width: '50%',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderLeft: '0',
                      borderBottomLeftRadius: '0',
                      borderTopLeftRadius: '0',
                    },
                  }}
                  error={Boolean(errors?.currencytype)}
                  helperText={errors?.currencytype?.message}
                />
              )}
            </>
          </AppBadge>
        </Grid>
      )}
      {(isDiscountBasedBrand || isListPriceBrand) && (
        <Grid xs={12} item>
          <AppBadge title={quantityBreakMessage} badgeContent='!' invisible={!quantityBreakFlag} fullWidth>
            <NumberTextField
              name='listPrice'
              label='List Price'
              prefix='$'
              rules={{
                required: {
                  message: 'List Price is required',
                  value: isNew,
                },
                min: {
                  message: 'List Price must be greater than 0',
                  value: 0.001,
                },
              }}
              error={Boolean(errors?.listPrice)}
              helperText={errors?.listPrice?.message || listPriceHelperText}
            />
          </AppBadge>
        </Grid>
      )}
      <Grid xs={12} item>
        <VendorAutocomplete
          defaultValue=''
          name='vendorName'
          error={Boolean(errors?.vendorName)}
          helperText={errors?.vendorName?.message}
          readOnly={!isRecommendation && !isNew}
          onSelect={onVendorSelect}
          onClear={onVendorClear}
        />
      </Grid>
      {newVendor && (
        <Grid xs={12} item>
          <FormTextField
            label='Vendor Id'
            defaultValue=''
            name='vendorId'
            rules={{
              required: {
                message: 'Vendor Id is required',
                value: newVendor,
              },
            }}
            error={Boolean(errors?.vendorId)}
            helperText={errors?.vendorId?.message}
            readOnly={false}
          />
        </Grid>
      )}
      {isNianticBrand && (
        <Grid xs={12} item>
          <CustomerProductNumberDropdown
            name='customerProductNumber'
            productId={productId}
            customerId={customerId}
            isNew={isNew}
          />
        </Grid>
      )}
      {isPartsVsEquipmentAvailable && (
        <Grid xs={12} item>
          <SelectField
            name='MROvsCapital'
            label={isInscoProccess ? 'MRO vs Capital' : 'Parts vs Equipment'}
            options={isInscoProccess ? ['MRO', 'CAPITAL'] : ['EQUIPMENT', 'PARTS']}
            getOptionLabel={option => option}
            getOptionSelected={(option, value) => option === value}
          />
        </Grid>
      )}
      {isCBEbrand && (
        <>
          <Grid xs={12} item>
            <ProductDropdown
              errorMessage='Factor Sheet Name is required'
              useQuery={useFactorSheetName}
              label='Factor Sheet Name'
              defaultValue=''
              name='factorSheetName'
              error={Boolean(errors?.factorSheetName)}
              helperText={errors?.factorSheetName?.message}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <NumberTextField
              name='freightadder'
              label='Freight Adder'
              decimalScale={4}
              rules={{
                required: {
                  message: 'Freight Adder is required',
                  value: true,
                },
              }}
              error={Boolean(errors?.freightadder)}
              helperText={errors?.freightadder?.message}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <ExchangeFormTextField
              name={'exchangerate'}
              defaultValue={exchangeRate || productExchangeRate}
              selectedCurrency={selectedCurrency}
              isNewProduct={isNew}
              readOnly
            />
          </Grid>
        </>
      )}
    </Grid>
  );
}
